import React, { useState, useEffect } from 'react';
import { incidentReportsAPI, caregiverProfilesAPI, usersAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Warning, Plus, PencilSimple, Trash, Eye, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import BodyDiagram from './BodyDiagram';

const INCIDENT_TYPES = [
  { value: 'fall', label: 'Fall' },
  { value: 'injury', label: 'Injury' },
  { value: 'medication_error', label: 'Medication Error' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'property_damage', label: 'Property Damage' },
  { value: 'elopement', label: 'Elopement' },
  { value: 'other', label: 'Other' }
];

const SEVERITY_LEVELS = [
  { value: 'minor', label: 'Minor', color: 'bg-slate-100 text-slate-800' },
  { value: 'moderate', label: 'Moderate', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'severe', label: 'Severe', color: 'bg-orange-100 text-orange-800' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' }
];

const STATUS_META = {
  open: { label: 'Open', color: 'bg-red-100 text-red-800' },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-800' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800' }
};

const emptyForm = () => ({
  incident_datetime: nowLocalInput(),
  location: '',
  incident_type: 'fall',
  severity: 'minor',
  description: '',
  immediate_actions: '',
  witnesses: [],
  caregiver_id: '',
  injuries: [],
  family_notified: false,
  family_notified_by: '',
  family_notified_at: '',
  family_notified_method: 'call',
  family_notified_person: '',
  follow_up_status: 'open',
  resolution_notes: ''
});

function nowLocalInput() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const IncidentReportsTab = ({ clientId, clientName, userRole }) => {
  const canEdit = userRole === 'supervisor' || userRole === 'super_admin' || userRole === 'admin';
  const [reports, setReports] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [witnessInput, setWitnessInput] = useState('');

  useEffect(() => {
    loadAll();
  }, [clientId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, cg, sup] = await Promise.all([
        incidentReportsAPI.list(clientId),
        caregiverProfilesAPI.getAll(),
        usersAPI.getAll('supervisor').catch(() => ({ data: [] }))
      ]);
      setReports(r.data || []);
      // Combine caregivers + supervisors for the "on duty" dropdown
      const cgList = (cg.data || []).filter(c => c.employment_status === 'active').map(c => ({
        id: c.user_id || c.id, name: c.name, role: 'caregiver'
      }));
      const supList = (sup.data || []).map(s => ({ id: s.id, name: s.name, role: 'supervisor' }));
      const merged = [...cgList, ...supList];
      const seen = new Set();
      setCaregivers(merged.filter(u => u.id && !seen.has(u.id) && seen.add(u.id)));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load incident reports');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setWitnessInput('');
    setIsDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      incident_datetime: (r.incident_datetime || '').slice(0, 16) || nowLocalInput(),
      location: r.location || '',
      incident_type: r.incident_type || 'fall',
      severity: r.severity || 'minor',
      description: r.description || '',
      immediate_actions: r.immediate_actions || '',
      witnesses: r.witnesses || [],
      caregiver_id: r.caregiver_id || '',
      injuries: r.injuries || [],
      family_notified: !!r.family_notified,
      family_notified_by: r.family_notified_by || '',
      family_notified_at: (r.family_notified_at || '').slice(0, 16),
      family_notified_method: r.family_notified_method || 'call',
      family_notified_person: r.family_notified_person || '',
      follow_up_status: r.follow_up_status || 'open',
      resolution_notes: r.resolution_notes || ''
    });
    setWitnessInput('');
    setIsDialogOpen(true);
  };

  const addWitness = () => {
    const v = witnessInput.trim();
    if (!v) return;
    setForm(f => ({ ...f, witnesses: [...(f.witnesses || []), v] }));
    setWitnessInput('');
  };

  const removeWitness = (idx) => {
    setForm(f => ({ ...f, witnesses: f.witnesses.filter((_, i) => i !== idx) }));
  };

  const setInjuries = (injuries) => setForm(f => ({ ...f, injuries }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!form.incident_datetime) {
      toast.error('Incident date & time is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        incident_datetime: form.incident_datetime,
        location: form.location || null,
        incident_type: form.incident_type,
        severity: form.severity,
        description: form.description.trim(),
        immediate_actions: form.immediate_actions || null,
        witnesses: form.witnesses,
        caregiver_id: form.caregiver_id || null,
        injuries: form.injuries,
        family_notified: !!form.family_notified,
        family_notified_by: form.family_notified ? (form.family_notified_by || null) : null,
        family_notified_at: form.family_notified ? (form.family_notified_at || null) : null,
        family_notified_method: form.family_notified ? form.family_notified_method : null,
        family_notified_person: form.family_notified ? (form.family_notified_person || null) : null,
        follow_up_status: form.follow_up_status,
        resolution_notes: form.resolution_notes || null
      };
      if (editingId) {
        await incidentReportsAPI.update(editingId, payload);
        toast.success('Incident report updated');
      } else {
        await incidentReportsAPI.create(payload);
        toast.success('Incident report filed');
      }
      setIsDialogOpen(false);
      setEditingId(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save incident report');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this incident report? This action is audit-logged.')) return;
    try {
      await incidentReportsAPI.delete(id);
      toast.success('Incident report deleted');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const severityBadge = (sev) => {
    const meta = SEVERITY_LEVELS.find(s => s.value === sev) || SEVERITY_LEVELS[0];
    return <Badge className={`${meta.color} rounded-full text-xs`}>{meta.label}</Badge>;
  };
  const statusBadge = (st) => {
    const meta = STATUS_META[st] || STATUS_META.open;
    return <Badge className={`${meta.color} rounded-full text-xs`}>{meta.label}</Badge>;
  };
  const typeLabel = (t) => (INCIDENT_TYPES.find(i => i.value === t) || {}).label || t;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="incident-reports-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold">Incident Reports</h2>
          <p className="text-sm text-muted-foreground">Log falls, injuries, medication errors, or other incidents. All entries are HIPAA-audited.</p>
        </div>
        {canEdit && (
          <Button className="rounded-xl gap-2" onClick={openCreate} data-testid="new-incident-btn">
            <Plus weight="bold" className="w-4 h-4" />
            New Incident Report
          </Button>
        )}
      </div>

      {reports.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Warning weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No incident reports</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {canEdit ? 'Click "New Incident Report" to file one.' : 'None on file for this client.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <Card key={r.id} className="rounded-2xl border border-border" data-testid={`incident-card-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0 mt-1">
                      <Warning weight="duotone" className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium text-sm">{typeLabel(r.incident_type)}</p>
                        {severityBadge(r.severity)}
                        {statusBadge(r.follow_up_status)}
                        {r.family_notified && (
                          <Badge className="bg-blue-100 text-blue-800 rounded-full text-xs">Family notified</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {r.incident_datetime ? format(new Date(r.incident_datetime), 'MMM d, yyyy h:mm a') : ''}
                        {r.location && <> · {r.location}</>}
                        <> · Filed by {r.created_by_name}</>
                      </p>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{r.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setViewing(r)} data-testid={`view-incident-${r.id}`}>
                      <Eye weight="bold" className="w-4 h-4" />
                    </Button>
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => openEdit(r)} data-testid={`edit-incident-${r.id}`}>
                          <PencilSimple weight="bold" className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive" onClick={() => handleDelete(r.id)} data-testid={`delete-incident-${r.id}`}>
                          <Trash weight="bold" className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <DialogTitle className="font-heading">Incident Report — {typeLabel(viewing.incident_type)}</DialogTitle>
                  <div className="flex gap-2">
                    {severityBadge(viewing.severity)}
                    {statusBadge(viewing.follow_up_status)}
                  </div>
                </div>
                <DialogDescription>
                  {viewing.incident_datetime ? format(new Date(viewing.incident_datetime), 'PPpp') : ''}
                  {viewing.location && <> · {viewing.location}</>}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-2 space-y-4 text-sm" data-testid="incident-view-content">
                <Section title="Description">{viewing.description}</Section>
                {viewing.immediate_actions && <Section title="Immediate Actions Taken">{viewing.immediate_actions}</Section>}
                {viewing.caregiver_id && (
                  <Section title="Caregiver on Duty">
                    {caregivers.find(c => c.id === viewing.caregiver_id)?.name || viewing.caregiver_id}
                  </Section>
                )}
                {viewing.witnesses && viewing.witnesses.length > 0 && (
                  <Section title="Witnesses">{viewing.witnesses.join(', ')}</Section>
                )}
                {viewing.injuries && viewing.injuries.length > 0 && (
                  <Section title="Injuries">
                    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
                      <BodyDiagram value={viewing.injuries} onChange={() => {}} readOnly />
                      <ul className="space-y-1 text-sm">
                        {viewing.injuries.map((inj, i) => (
                          <li key={i}>
                            <span className="font-medium">{inj.body_part}</span>
                            {inj.description && <span className="text-muted-foreground"> — {inj.description}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Section>
                )}
                <Section title="Family Notified">
                  {viewing.family_notified ? (
                    <div className="text-sm">
                      Yes{viewing.family_notified_person ? ` — ${viewing.family_notified_person}` : ''}
                      {viewing.family_notified_method && ` via ${viewing.family_notified_method.replace('_', ' ')}`}
                      {viewing.family_notified_at && ` on ${format(new Date(viewing.family_notified_at), 'PPp')}`}
                      {viewing.family_notified_by && ` by ${viewing.family_notified_by}`}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Not notified</span>
                  )}
                </Section>
                {viewing.resolution_notes && <Section title="Follow-up / Resolution Notes">{viewing.resolution_notes}</Section>}
                {viewing.resolved_at && (
                  <p className="text-xs text-muted-foreground">
                    Resolved by {viewing.resolved_by_name} on {format(new Date(viewing.resolved_at), 'PPp')}
                  </p>
                )}
                <p className="text-xs text-muted-foreground pt-2 border-t">
                  Filed by {viewing.created_by_name} on {format(new Date(viewing.created_at), 'PPp')}
                  {viewing.updated_at !== viewing.created_at && ` · Last updated ${format(new Date(viewing.updated_at), 'PPp')}`}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingId ? 'Edit Incident Report' : 'New Incident Report'}
            </DialogTitle>
            <DialogDescription>
              {clientName ? `Filing an incident for ${clientName}.` : 'File a detailed incident report.'} All entries are HIPAA-audited.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date & Time of Incident *</Label>
                <Input
                  type="datetime-local"
                  value={form.incident_datetime}
                  onChange={(e) => setForm({ ...form, incident_datetime: e.target.value })}
                  className="rounded-xl"
                  required
                  data-testid="incident-datetime-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. Bathroom, kitchen, community outing"
                  className="rounded-xl"
                  data-testid="incident-location-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select value={form.incident_type} onValueChange={(v) => setForm({ ...form, incident_type: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="incident-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INCIDENT_TYPES.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severity *</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="incident-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITY_LEVELS.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Follow-up Status *</Label>
                <Select value={form.follow_up_status} onValueChange={(v) => setForm({ ...form, follow_up_status: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="incident-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description of Incident *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What happened? Provide detailed facts only."
                rows={4}
                className="rounded-xl"
                required
                data-testid="incident-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Immediate Actions Taken</Label>
              <Textarea
                value={form.immediate_actions}
                onChange={(e) => setForm({ ...form, immediate_actions: e.target.value })}
                placeholder="First aid, comfort measures, supervisor called, etc."
                rows={3}
                className="rounded-xl"
                data-testid="incident-actions-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Caregiver on Duty</Label>
                <Select
                  value={form.caregiver_id || 'none'}
                  onValueChange={(v) => setForm({ ...form, caregiver_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="incident-caregiver-select"><SelectValue placeholder="Select caregiver" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None / Not on shift —</SelectItem>
                    {caregivers.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.role === 'supervisor' ? ' (Supervisor)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Witnesses</Label>
                <div className="flex gap-2">
                  <Input
                    value={witnessInput}
                    onChange={(e) => setWitnessInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addWitness(); } }}
                    placeholder="Type a name and press Enter"
                    className="rounded-xl"
                    data-testid="incident-witness-input"
                  />
                  <Button type="button" variant="outline" className="rounded-xl" onClick={addWitness} data-testid="add-witness-btn">Add</Button>
                </div>
                {form.witnesses.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {form.witnesses.map((w, i) => (
                      <Badge key={i} variant="secondary" className="rounded-full gap-1">
                        {w}
                        <button type="button" onClick={() => removeWitness(i)} className="hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Body diagram / Injuries */}
            <div className="space-y-2">
              <Label>Injuries — Body Diagram</Label>
              <p className="text-xs text-muted-foreground">Click a body region to mark an injury. Add a note describing each one.</p>
              <div className="rounded-xl border border-border p-3 bg-muted/20">
                <BodyDiagram value={form.injuries} onChange={setInjuries} />
              </div>
            </div>

            {/* Family notified */}
            <div className="rounded-xl border border-border p-3 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.family_notified}
                  onChange={(e) => setForm({ ...form, family_notified: e.target.checked })}
                  className="rounded"
                  data-testid="incident-family-notified"
                />
                <span className="font-medium text-sm">Family / Emergency Contact Notified</span>
              </label>
              {form.family_notified && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Notified Whom</Label>
                    <Input
                      value={form.family_notified_person}
                      onChange={(e) => setForm({ ...form, family_notified_person: e.target.value })}
                      placeholder="e.g. Daughter — Sarah Miller"
                      className="rounded-lg h-9"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Method</Label>
                    <Select value={form.family_notified_method} onValueChange={(v) => setForm({ ...form, family_notified_method: v })}>
                      <SelectTrigger className="rounded-lg h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="call">Phone Call</SelectItem>
                        <SelectItem value="text">Text Message</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="in_person">In Person</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Notified At</Label>
                    <Input
                      type="datetime-local"
                      value={form.family_notified_at}
                      onChange={(e) => setForm({ ...form, family_notified_at: e.target.value })}
                      className="rounded-lg h-9"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Notified By</Label>
                    <Input
                      value={form.family_notified_by}
                      onChange={(e) => setForm({ ...form, family_notified_by: e.target.value })}
                      placeholder="Your name / staff who called"
                      className="rounded-lg h-9"
                    />
                  </div>
                </div>
              )}
            </div>

            {(form.follow_up_status === 'under_review' || form.follow_up_status === 'resolved') && (
              <div className="space-y-2">
                <Label>{form.follow_up_status === 'resolved' ? 'Resolution Notes' : 'Follow-up Notes'}</Label>
                <Textarea
                  value={form.resolution_notes}
                  onChange={(e) => setForm({ ...form, resolution_notes: e.target.value })}
                  placeholder={form.follow_up_status === 'resolved' ? 'How was this resolved? What corrective actions were taken?' : 'What follow-up is being done?'}
                  rows={3}
                  className="rounded-xl"
                  data-testid="incident-resolution-input"
                />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsDialogOpen(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" disabled={saving} data-testid="save-incident-btn">
                {saving ? 'Saving...' : (editingId ? 'Save Changes' : 'File Incident Report')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-1">{title}</p>
    <div className="whitespace-pre-wrap">{children}</div>
  </div>
);

export default IncidentReportsTab;
